import { PROVIDER_PERMISSIONS } from "@tellescope/constants";
import { TellescopeModelFromName, useRolesQuery, useUsersQuery } from "@tellescope/tanstack-query";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOrgFeatures } from "../../../../permissions";
import {
  DATA_ACCESS_FEATURES,
  DEFAULT_ADMIN_ROLE,
  DEFAULT_NON_ADMIN_ROLE,
  isFeatureAllowedForPlan,
} from "./roleEditorConfig";

type RolePermissions = TellescopeModelFromName<"role_based_access_permissions">["permissions"];

const ACCESS_ACTIONS = ["create", "read", "update", "delete"] as const;

export interface RolesFilters {
  /** Limit to roles that do/don't have team members assigned. */
  assignment?: "assigned" | "unassigned";
  /** Limit to roles that do/don't grant any permissions. */
  permissions?: "with" | "without";
}

export interface RoleCardData {
  id: string;
  role: string;
  color?: string;
  description?: string;
  userCount: number;
  permissionCount: number;
  /** A built-in fallback role (e.g. Admin / Non-Admin) that has no stored record and can't be edited. */
  isDefault?: boolean;
}

const countPermissions = (permissions: RolePermissions, allowed: Set<string>) =>
  Object.entries(permissions).filter(
    ([resource, access]) =>
      allowed.has(resource) && !!access && ACCESS_ACTIONS.some((action) => Boolean(access[action]))
  ).length;

export const useRolesSubPage = () => {
  const { t } = useTranslation("settings");
  const { plan } = useOrgFeatures();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);
  const [filters, setFilters] = useState<RolesFilters>({});

  // Resources belonging to features the org's plan exposes — the count mirrors what the editor shows.
  const allowedResources = useMemo(
    () =>
      new Set(
        DATA_ACCESS_FEATURES.reduce<string[]>(
          (resources, feature) =>
            isFeatureAllowedForPlan(plan, feature.key) ? [...resources, ...feature.resources] : resources,
          []
        )
      ),
    [plan]
  );

  const trimmedSearch = debouncedSearchQuery.trim();
  const rolesQuery = useRolesQuery();
  const usersQuery = useUsersQuery();

  const usersByRole = useMemo(
    () =>
      (usersQuery.data ?? []).reduce(
        (counts, user) => (user.roles ?? []).reduce((acc, role) => acc.set(role, (acc.get(role) ?? 0) + 1), counts),
        new Map<string, number>()
      ),
    [usersQuery.data]
  );

  // Team members with no role fall back to the implicit non-admin (provider) baseline.
  const usersWithoutRoleCount = useMemo(
    () => (usersQuery.data ?? []).filter((user) => (user.roles?.length ?? 0) === 0).length,
    [usersQuery.data]
  );

  // The Admin and Non-Admin roles aren't stored in role_based_access_permissions, so they never come back
  // from the query — surface them as read-only default cards alongside the org's custom roles.
  const defaultRoleCards = useMemo<RoleCardData[]>(() => {
    const cards: RoleCardData[] = [
      {
        id: "default-admin",
        role: DEFAULT_ADMIN_ROLE.role,
        color: DEFAULT_ADMIN_ROLE.color,
        description: t("Full access to all data and settings. Applies to anyone with the Admin role."),
        userCount: usersByRole.get(DEFAULT_ADMIN_ROLE.role) ?? 0,
        permissionCount: countPermissions(
          { ...PROVIDER_PERMISSIONS, ...DEFAULT_ADMIN_ROLE.permissions },
          allowedResources
        ),
        isDefault: true,
      },
      {
        id: "default-non-admin",
        role: t("Non-Admin"),
        color: DEFAULT_NON_ADMIN_ROLE.color,
        description: t("Baseline access applied to team members without a custom role."),
        userCount: usersWithoutRoleCount,
        permissionCount: countPermissions(
          { ...PROVIDER_PERMISSIONS, ...DEFAULT_NON_ADMIN_ROLE.permissions },
          allowedResources
        ),
        isDefault: true,
      },
    ];

    const query = trimmedSearch.toLowerCase();
    return query ? cards.filter((card) => card.role.toLowerCase().includes(query)) : cards;
  }, [t, usersByRole, usersWithoutRoleCount, allowedResources, trimmedSearch]);

  const roleCards = useMemo<RoleCardData[]>(() => {
    const customCards = (rolesQuery.data ?? [])
      .map((role) => {
        // Resources the role doesn't list inherit the provider baseline at auth time, so count the
        // effective (merged) permissions rather than only the explicitly-stored overrides.
        const effectivePermissions = { ...PROVIDER_PERMISSIONS, ...role.permissions };

        return {
          id: role.id,
          role: role.role,
          color: role.color,
          description: role.description,
          userCount: usersByRole.get(role.role) ?? 0,
          permissionCount: countPermissions(effectivePermissions, allowedResources),
        };
      })
      .sort((a, b) => a.role.localeCompare(b.role));

    return [...defaultRoleCards, ...customCards];
  }, [rolesQuery.data, defaultRoleCards, usersByRole, allowedResources]);

  const filteredRoles = useMemo(() => {
    return roleCards.filter((card) => {
      if (filters.assignment === "assigned" && card.userCount === 0) return false;
      if (filters.assignment === "unassigned" && card.userCount > 0) return false;
      if (filters.permissions === "with" && card.permissionCount === 0) return false;
      if (filters.permissions === "without" && card.permissionCount > 0) return false;
      return true;
    });
  }, [roleCards, filters]);

  const activeFiltersCount = (filters.assignment ? 1 : 0) + (filters.permissions ? 1 : 0);

  return {
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    filteredRoles,
    activeFiltersCount,
    isLoading: rolesQuery.isLoading || usersQuery.isLoading || searchQuery !== debouncedSearchQuery,
  };
};
